import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { usePrefs } from '@/store/prefs';
import { getTool } from '@/registry/tools';
import { cn } from '@/lib/cn';

/**
 * Workspace tabs — one chip per open tool. Click a chip to focus, the X to
 * close. The "Browser" tab is implicit: when no tab is active, we render the
 * tool browser in the main area. Closing the last tab returns there.
 *
 * Overflow handling: when there are too many tabs to fit on a single row,
 * left/right chevron buttons appear and smooth-scroll the strip. The buttons
 * auto-disable when there's nothing to scroll on that side.
 */
const SCROLL_STEP = 140;

export function TabBar() {
  const tabs = usePrefs((s) => s.tabs);
  const activeTabId = usePrefs((s) => s.activeTabId);
  const setActiveTab = usePrefs((s) => s.setActiveTab);
  const closeTab = usePrefs((s) => s.closeTab);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanLeft(el.scrollLeft > 0);
      setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [tabs.length]);

  // Keep the active tab in view when it changes (e.g. opened from the dock).
  useEffect(() => {
    if (!activeTabId) return;
    const el = scrollRef.current;
    if (!el) return;
    const target = el.querySelector<HTMLElement>(`[data-tab-id="${activeTabId}"]`);
    target?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [activeTabId]);

  if (tabs.length === 0) return null;

  const scrollBy = (dx: number) =>
    scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  return (
    <div className="flex shrink-0 items-stretch border-b border-border bg-background/95">
      <ArrowButton
        direction="left"
        disabled={!canLeft}
        onClick={() => scrollBy(-SCROLL_STEP)}
      />
      <div
        ref={scrollRef}
        role="tablist"
        className="no-scrollbar flex flex-1 items-center gap-0.5 overflow-x-auto px-1 py-1"
      >
        {tabs.map((tab) => {
          const tool = getTool(tab.toolId);
          if (!tool) return null;
          const Icon = tool.icon;
          const active = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              className={cn(
                'group flex shrink-0 cursor-pointer items-center gap-1 rounded-md border px-1.5 py-0.5 text-2xs transition-colors',
                active
                  ? 'border-border bg-secondary text-foreground'
                  : 'border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
              onClick={() => setActiveTab(tab.id)}
              onMouseDown={(e) => {
                if (e.button === 1) {
                  e.preventDefault();
                  closeTab(tab.id);
                }
              }}
              role="tab"
              aria-selected={active}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveTab(tab.id);
                }
              }}
            >
              <Icon className="h-3 w-3 shrink-0" />
              <span className="max-w-[6rem] truncate">{tool.name}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.id);
                }}
                className={cn(
                  'rounded p-0.5 transition-opacity hover:bg-background',
                  active ? 'opacity-70 hover:opacity-100' : 'opacity-0 group-hover:opacity-70',
                )}
                aria-label={`Close ${tool.name}`}
                title={`Close ${tool.name}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          );
        })}
      </div>
      <ArrowButton
        direction="right"
        disabled={!canRight}
        onClick={() => scrollBy(SCROLL_STEP)}
      />
    </div>
  );
}

function ArrowButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll tabs left' : 'Scroll tabs right'}
      title={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={cn(
        'flex w-6 shrink-0 items-center justify-center text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled && 'pointer-events-none opacity-25',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
